import type {RelayLease} from '@github-ui/ahp-relay/relay-registry'
import {acquireRelayLease} from '@github-ui/ahp-relay/relay-registry'
import {getInputRequests} from '@github-ui/ahp-relay/ahp-session-selectors'
import {AhpSessionClient} from './session-client'
import type {
  EstablishedAhpPlanningPolicy,
  EstablishedAhpPlan,
  EstablishedAhpPlanningState,
  EstablishedAhpSession,
  EstablishedAhpSessionLifecycle,
  EstablishedAhpSessionPlanning,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpLifecycleState,
} from './contracts'
import {EstablishedAhpSessionError as SessionError} from './contracts'
import {relayFailureFromError} from '@github-ui/ahp-relay/relay-failure'

interface EstablishedAhpSessionRuntime {
  readonly session: EstablishedAhpSession
  start(): Promise<void>
  waitForAccepted(receipt: Parameters<AhpSessionClient['waitForAccepted']>[0]): Promise<void>
  reset(): void
  dispose(): void
}

interface EstablishedAhpSessionRuntimeOptions extends EstablishedAhpSessionProvisioned {
  readonly planning?: EstablishedAhpPlanningPolicy
}

function lifecycleStateFor(status: ReturnType<RelayLease['getStatus']>): EstablishedAhpLifecycleState {
  switch (status) {
    case 'connected':
      return 'live'
    case 'reconnecting':
      return 'reconnecting'
    case 'error':
    case 'closed':
      return 'failed'
    default:
      return 'connecting'
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The established AHP session failed.'
}

/**
 * Internal factory used by bootstrap coordinators after they have resolved a
 * full session identity and relay adapter. It keeps the relay lease and client
 * private to the runtime returned to the bootstrap.
 */
export async function createEstablishedAhpSessionRuntime(
  options: EstablishedAhpSessionRuntimeOptions,
): Promise<EstablishedAhpSessionRuntime> {
  if (options.planning?.enabled && !options.planning.adapter) {
    throw new SessionError(
      'establish',
      'connecting',
      'Active-AHP planning is enabled but no planning adapter is configured.',
      false,
    )
  }

  const relay = {
    ...options.relay,
    environmentId: options.identity.environmentId,
    sessionId: options.identity.sessionId,
    ...(options.identity.channel ? {channel: options.identity.channel} : {}),
    ...(options.identity.taskId ? {taskId: options.identity.taskId} : {}),
  }
  const lease = await acquireRelayLease(relay)
  const client = new AhpSessionClient(options.identity, lease, options.directTurnTelemetry)
  const lifecycleListeners = new Set<() => void>()
  let lifecycleState: EstablishedAhpLifecycleState = lifecycleStateFor(lease.getStatus())
  let disposed = false
  let planningState: EstablishedAhpPlanningState = {status: 'idle'}

  const setLifecycleState = (next: EstablishedAhpLifecycleState) => {
    if (next === lifecycleState) return
    lifecycleState = next
    for (const listener of lifecycleListeners) listener()
  }

  const unsubscribeRelay = lease.subscribe(() => {
    if (disposed || !lease.isCurrent()) return
    setLifecycleState(lifecycleStateFor(lease.getStatus()))
  })

  const lifecycle: EstablishedAhpSessionLifecycle = {
    getState: () => lifecycleState,
    subscribe: listener => {
      lifecycleListeners.add(listener)
      return () => lifecycleListeners.delete(listener)
    },
  }

  const planningAdapter = options.planning?.enabled ? options.planning.adapter : undefined
  const refreshPlanningState = () => {
    if (!planningAdapter) return
    try {
      const state = client.view.getSnapshot().state
      let plan: EstablishedAhpPlan | undefined
      if (state) {
        for (const request of getInputRequests(state)) {
          plan = planningAdapter.identify(request)
          if (plan) break
        }
      }
      if (plan && plan.id !== planningState.plan?.id) {
        planningState = {status: 'pending', plan}
      } else if (!plan && planningState.status === 'pending') {
        planningState = {status: 'idle'}
      }
    } catch (error) {
      planningState = {status: 'failed', error: errorMessage(error)}
    }
  }
  const unsubscribePlanning = planningAdapter ? client.view.subscribe(refreshPlanningState) : () => {}
  refreshPlanningState()
  const planning: EstablishedAhpSessionPlanning | undefined = options.planning?.enabled
    ? {
        get state() {
          return planningState
        },
        actions: {
          approve: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to approve')
            try {
              await planningAdapter.approve(plan)
              planningState = {status: 'approved', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          reject: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to reject')
            try {
              await planningAdapter.reject(plan)
              planningState = {status: 'rejected', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          revise: async prompt => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to revise')
            try {
              await planningAdapter.revise(plan, prompt)
              planningState = {status: 'pending', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          continue: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to continue')
            try {
              await planningAdapter.continue(plan)
              planningState = {status: 'approved', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
        },
      }
    : undefined

  const session: EstablishedAhpSession = {
    identity: options.identity,
    view: client.view,
    commands: client.commands,
    input: client.input,
    ...(planning ? {planning} : {}),
    lifecycle,
  }

  const runtime: EstablishedAhpSessionRuntime = {
    session,
    start: async () => {
      if (disposed) throw new Error('established AHP session runtime is disposed')
      try {
        await lease.whenConnected()
        setLifecycleState(lifecycleStateFor(lease.getStatus()))
      } catch (error) {
        setLifecycleState('failed')
        throw new SessionError('start', lifecycleState, errorMessage(error), true, {
          cause: error,
          failure: relayFailureFromError(error),
        })
      }
    },
    waitForAccepted: receipt => client.waitForAccepted(receipt),
    reset: () => {
      runtime.dispose()
    },
    dispose: () => {
      if (disposed) return
      disposed = true
      setLifecycleState('disposed')
      unsubscribeRelay()
      unsubscribePlanning()
      client.markDisposed()
      lease.release()
    },
  }

  return runtime
}
